import React, { useState, useEffect } from 'react';
import { getUsers } from '../mock/users';

const ClientAssignmentModal = ({ 
  isOpen, 
  onClose, 
  client, 
  onAssign,
  currentUser 
}) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      // Pre-seleccionar usuarios ya asignados al cliente
      if (client && client.salesExecutive) {
        const assignedUsers = client.salesExecutive.split(',').map(u => u.trim());
        setSelectedUsers(assignedUsers);
      } else {
        setSelectedUsers([]);
      }
    }
  }, [isOpen, client]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getUsers();
      
      // Filtrar usuarios según el rol del usuario actual
      let filteredUsers = usersData;
      
      if (currentUser?.role !== 'admin') {
        // Si no es admin, solo mostrar usuarios de las mismas células
        const currentUserCells = currentUser?.cells || [];
        filteredUsers = usersData.filter(user => {
          const userCells = user.cells || [];
          return userCells.some(cell => currentUserCells.includes(cell));
        });
      }
      
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (user) => {
    const userFullName = `${user.firstName} ${user.lastName}`.trim();
    
    if (selectedUsers.includes(userFullName)) {
      setSelectedUsers(selectedUsers.filter(u => u !== userFullName));
    } else {
      setSelectedUsers([...selectedUsers, userFullName]);
    }
  };

  const handleAssign = async () => {
    try {
      setLoading(true);
      await onAssign(client.id, selectedUsers);
      onClose();
    } catch (error) {
      console.error('Error asignando cliente:', error);
      alert('Error al asignar cliente: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return fullName.includes(search) || email.includes(search);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white">
                Asignar Cliente a Asesores
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                Cliente: {client?.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar asesores por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0061A8] focus:border-transparent"
              />
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Selected Users Summary */}
          {selectedUsers.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-semibold text-[#0061A8] mb-2">
                Asesores Seleccionados ({selectedUsers.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((user, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#0061A8] text-white"
                  >
                    {user}
                    <button
                      onClick={() => setSelectedUsers(selectedUsers.filter(u => u !== user))}
                      className="ml-2 hover:text-blue-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Users List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0061A8]"></div>
                <span className="ml-3 text-gray-600">Cargando asesores...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No se encontraron asesores con ese criterio' : 'No hay asesores disponibles'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.map((user) => {
                  const userFullName = `${user.firstName} ${user.lastName}`.trim();
                  const isSelected = selectedUsers.includes(userFullName);
                  
                  return (
                    <div
                      key={user.id}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'border-[#0061A8] bg-blue-50'
                          : 'border-gray-200 hover:border-[#0061A8] hover:bg-gray-50'
                      }`}
                      onClick={() => handleUserToggle(user)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-[#0061A8] border-[#0061A8]' : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {userFullName}
                            </h4>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            {user.cells && user.cells.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {user.cells.map((cell, index) => (
                                  <span
                                    key={index}
                                    className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                                  >
                                    {cell}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 capitalize">
                          {user.role}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || selectedUsers.length === 0}
            className="px-6 py-2 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white rounded-xl hover:from-[#004d8a] hover:to-[#0061A8] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Asignando...
              </div>
            ) : (
              `Asignar Cliente (${selectedUsers.length})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientAssignmentModal;
